<!-- Page Header -->
<div class="mb-4">
  <h1 class="h2 mb-1 fw-bold">State Reports</h1>
  <p class="text-muted mb-0">Generate compliance reports for state agricultural and environmental agencies</p>
</div>

<!-- Error Alert -->
<div *ngIf="error()" class="alert alert-danger alert-dismissible fade show mb-4" role="alert">
  <i class="bi bi-exclamation-triangle-fill me-2"></i>
  {{ error() }}
  <button type="button" class="btn-close" (click)="clearError()"></button>
  <div class="mt-2">
    <button class="btn btn-sm btn-outline-danger" (click)="retryLoad()">
      <i class="bi bi-arrow-clockwise me-1"></i>Retry
    </button>
  </div>
</div>

<div class="row g-4">
  <!-- Report Generator -->
  <div class="col-lg-5">
    <div class="card">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-file-earmark-plus me-2"></i>Generate Report</h5>
      </div>
      <div class="card-body">
        <!-- State Selection -->
        <div class="mb-4">
          <label class="form-label fw-medium">State <span class="text-danger">*</span></label>
          <select class="form-select" [(ngModel)]="selectedState" [class.is-invalid]="!selectedState">
            <option value="">Select a state...</option>
            <option *ngFor="let state of states" [value]="state.value">
              {{ state.label }}
            </option>
          </select>
          <div class="form-text">Select the state for which to generate the compliance report.</div>
        </div>

        <!-- Date Range -->
        <div class="mb-4">
          <label class="form-label fw-medium">Date Range</label>
          <div class="row g-2">
            <div class="col-6">
              <label class="form-label small text-muted">Start Date</label>
              <input type="date" class="form-control" [(ngModel)]="startDate">
            </div>
            <div class="col-6">
              <label class="form-label small text-muted">End Date</label>
              <input type="date" class="form-control" [(ngModel)]="endDate">
            </div>
          </div>
        </div>

        <!-- Report Type: intentionally not offered.
             Every state report is generated as "All Applications".

             The type list and the spray-record `application_types` list were
             never reconciled, so three of the four filters were wrong:
               - `pesticide` matched only records literally tagged `pesticide`,
                 omitting herbicide and fungicide applications, which ARE
                 pesticides to a regulator — an under-reported filing;
               - `seeding` matched nothing at all; and
               - `herbicide` / `fungicide` could not be selected.
             The PDF never rendered the type, so filtering only ever *removed*
             records from a compliance document.

             `reportType` stays pinned to 'all' in the component, and the Hub
             still honours a specific type for API callers and for retrying
             historical rows — so this is reversible the day the vocabularies
             are reconciled. See the follow-up ticket in
             agent-os/specs/2026-08-11-spray-record-number-of-flights/implementation-log.md -->

        <!-- Include Options -->
        <div class="mb-4">
          <label class="form-label fw-medium">Include in Report</label>
          <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" id="chemicalDetails" [(ngModel)]="includeChemicalDetails">
            <label class="form-check-label" for="chemicalDetails">Chemical Details</label>
          </div>
          <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" id="applicationRates" [(ngModel)]="includeApplicationRates">
            <label class="form-check-label" for="applicationRates">Application Rates</label>
          </div>
          <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" id="fieldMaps" [(ngModel)]="includeFieldMaps">
            <label class="form-check-label" for="fieldMaps">Field Maps</label>
          </div>
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="weatherConditions" [(ngModel)]="includeWeatherConditions">
            <label class="form-check-label" for="weatherConditions">Weather Conditions</label>
          </div>
        </div>

        <!-- Generate Button -->
        <button
          class="btn btn-primary w-100"
          (click)="generateReport()"
          [disabled]="isGenerating() || !canGenerate()"
        >
          <ng-container *ngIf="!isGenerating()">
            <i class="bi bi-file-earmark-pdf me-2"></i>Generate PDF Report
          </ng-container>
          <ng-container *ngIf="isGenerating()">
            <span class="spinner-border spinner-border-sm me-2" role="status"></span>
            Generating...
          </ng-container>
        </button>
      </div>
    </div>

    <!-- Info Card -->
    <div class="card mt-4">
      <div class="card-body">
        <h6 class="d-flex align-items-center gap-2 mb-3">
          <i class="bi bi-info-circle text-primary"></i>
          About State Reports
        </h6>
        <p class="small text-muted mb-2">
          State reports are formatted to meet specific state agricultural department requirements for pesticide and fertilizer applications.
        </p>
        <ul class="small text-muted mb-0 ps-3">
          <li>Pesticide reports include EPA registration numbers</li>
          <li>Application rates are reported per state standards</li>
          <li>Field coordinates can be included for location documentation</li>
          <li>All reports are exportable as PDF</li>
        </ul>
      </div>
    </div>
  </div>

  <!-- Recent Reports -->
  <div class="col-lg-7">
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center p-3">
        <h5 class="mb-0">Recent Reports</h5>
        <span class="badge bg-secondary">{{ recentReports().length }} reports</span>
      </div>
      <div class="card-body p-0">
        <!-- Loading State -->
        <ng-container *ngIf="isLoading()">
          <div class="p-5 text-center">
            <div class="spinner-border text-primary mb-3" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="text-muted mb-0">Loading reports...</p>
          </div>
        </ng-container>

        <!-- Reports List -->
        <ng-container *ngIf="!isLoading()">
          <ng-container *ngIf="recentReports().length > 0; else emptyState">
            <ng-container *ngFor="let report of recentReports(); let last = last">
              <div class="report-item p-3" [class.border-bottom]="!last">
                <div class="d-flex justify-content-between align-items-start">
                  <div class="d-flex align-items-start gap-3 flex-grow-1">
                    <div class="report-icon" [ngClass]="'status-' + report.status">
                      <i class="bi" [ngClass]="getStatusIcon(report.status)"
                         [class.spin]="report.status === 'generating'"></i>
                    </div>
                    <div class="flex-grow-1">
                      <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <h6 class="mb-0 fw-medium">{{ report.name || getReportTypeLabel(report.reportType) }}</h6>
                        <span class="badge" [ngClass]="report.status | statusBadge:'report'">
                          <i class="bi me-1" [ngClass]="getStatusIcon(report.status)"
                             [class.spin]="report.status === 'generating'"></i>
                          {{ getStatusText(report.status) }}
                        </span>
                      </div>
                      <div class="small text-muted mb-1">
                        <i class="bi bi-geo-alt me-1"></i>{{ getStateLabel(report.state) }} &bull; {{ formatDateRange(report.startDate, report.endDate) }}
                      </div>

                      <!-- Progress bar for generating status -->
                      <div *ngIf="report.status === 'generating'" class="progress-container mb-2">
                        <div class="progress h-6px">
                          <div class="progress-bar progress-bar-striped progress-bar-animated"
                               role="progressbar"
                               [style.width.%]="report.generationProgress || 0"
                               [attr.aria-valuenow]="report.generationProgress || 0"
                               aria-valuemin="0"
                               aria-valuemax="100">
                          </div>
                        </div>
                        <span class="small text-muted">{{ report.generationProgress || 0 }}% complete</span>
                      </div>

                      <!-- Error message for failed reports -->
                      <div *ngIf="report.status === 'failed' && report.errorMessage" class="error-info mb-1">
                        <span class="small text-danger">
                          <i class="bi bi-exclamation-circle me-1"></i>
                          {{ report.errorMessage }}
                        </span>
                      </div>

                      <!-- Submission info for submitted reports -->
                      <div *ngIf="report.status === 'submitted' && report.submittedTo" class="py-1 mb-1">
                        <span class="small text-success">
                          <i class="bi bi-check-circle me-1"></i>
                          Sent to {{ report.submittedTo }} on {{ report.submittedAt | adnDate:'datetime' }}
                        </span>
                      </div>

                      <div class="d-flex flex-wrap gap-3 small">
                        <div>
                          <i class="bi bi-clock me-1"></i>
                          {{ report.createdAt | adnDate:'datetime' }}
                        </div>
                        <div *ngIf="report.recordCount && report.recordCount > 0">
                          <i class="bi bi-list-ul me-1"></i>
                          {{ report.recordCount }} records
                        </div>
                        <div *ngIf="report.fileSize">
                          <i class="bi bi-file-earmark me-1"></i>
                          {{ report.fileSize }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="flex-shrink-0 ms-3">
                    <div class="d-flex flex-column gap-2">
                      <!-- Action buttons based on status -->
                      <ng-container [ngSwitch]="report.status">
                        <!-- Draft: Generate PDF button -->
                        <ng-container *ngSwitchCase="'draft'">
                          <button class="btn btn-sm btn-primary" (click)="generatePdf(report)">
                            <i class="bi bi-file-earmark-pdf me-1"></i>Generate PDF
                          </button>
                          <button class="btn btn-sm btn-outline-secondary" (click)="viewReport(report)">
                            <i class="bi bi-eye me-1"></i>Preview
                          </button>
                        </ng-container>

                        <!-- Generating: Disabled with spinner -->
                        <ng-container *ngSwitchCase="'generating'">
                          <button class="btn btn-sm btn-outline-secondary" disabled>
                            <span class="spinner-border spinner-border-sm me-1"></span>
                            Generating...
                          </button>
                        </ng-container>

                        <!-- Ready: Download, View, Email buttons -->
                        <ng-container *ngSwitchCase="'ready'">
                          <div class="btn-group btn-group-sm">
                            <button class="btn btn-outline-primary" (click)="downloadReport(report)" title="Download PDF">
                              <i class="bi bi-download"></i>
                            </button>
                            <button class="btn btn-outline-secondary" (click)="viewReport(report)" title="Preview">
                              <i class="bi bi-eye"></i>
                            </button>
                            <button class="btn btn-outline-secondary" (click)="openEmailModal(report)" title="Send to Agency">
                              <i class="bi bi-envelope"></i>
                            </button>
                          </div>
                        </ng-container>

                        <!-- Submitted: Download, View, and re-send (nothing was sent by ADN) -->
                        <ng-container *ngSwitchCase="'submitted'">
                          <div class="btn-group btn-group-sm">
                            <button class="btn btn-outline-primary" (click)="downloadReport(report)" title="Download PDF">
                              <i class="bi bi-download"></i>
                            </button>
                            <button class="btn btn-outline-secondary" (click)="viewReport(report)" title="Preview">
                              <i class="bi bi-eye"></i>
                            </button>
                            <button class="btn btn-outline-secondary" (click)="openEmailModal(report)" title="Send to Agency Again">
                              <i class="bi bi-envelope"></i>
                            </button>
                          </div>
                        </ng-container>

                        <!-- Failed: Retry button -->
                        <ng-container *ngSwitchCase="'failed'">
                          <button class="btn btn-sm btn-warning" (click)="retryReport(report)">
                            <i class="bi bi-arrow-clockwise me-1"></i>Retry
                          </button>
                        </ng-container>
                      </ng-container>

                      <!-- Delete button (always available except during generation) -->
                      <button
                        *ngIf="report.status !== 'generating'"
                        class="btn btn-sm btn-outline-danger"
                        (click)="confirmDelete(report)"
                        title="Delete"
                      >
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </ng-container>
          </ng-container>

          <ng-template #emptyState>
            <div class="p-5 text-center">
              <div class="empty-state-icon mb-3">
                <i class="bi bi-file-earmark-text"></i>
              </div>
              <h5>No Reports Generated</h5>
              <p class="text-muted mb-0">Generate your first state report using the form on the left.</p>
            </div>
          </ng-template>
        </ng-container>
      </div>
    </div>
  </div>
</div>

<!-- Preview Modal -->
<div class="modal-backdrop fade show" *ngIf="showPreviewModal()" (click)="closePreviewModal()"></div>
<div class="modal fade show d-block" tabindex="-1" *ngIf="showPreviewModal()">
  <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-file-earmark-text me-2"></i>Report Preview
        </h5>
        <button type="button" class="btn-close" (click)="closePreviewModal()"></button>
      </div>
      <div class="modal-body" *ngIf="previewReport()">
        <ng-container *ngIf="previewLoading(); else previewContent">
          <div class="text-center py-5">
            <div class="spinner-border text-primary mb-3" role="status"></div>
            <p class="text-muted">Loading report preview...</p>
          </div>
        </ng-container>

        <ng-template #previewContent>
          <!-- Report Header -->
          <div class="report-preview-header mb-4">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h4 class="mb-1">{{ previewReport()?.name || getReportTypeLabel(previewReport()?.reportType || '') }}</h4>
                <p class="text-muted mb-0">
                  <i class="bi bi-geo-alt me-1"></i>{{ getStateLabel(previewReport()?.state || '') }} &bull; {{ formatDateRange(previewReport()?.startDate || '', previewReport()?.endDate || '') }}
                </p>
              </div>
              <div class="text-end">
                <span class="badge" [ngClass]="previewReport()?.status | statusBadge:'report'">
                  {{ getStatusText(previewReport()?.status || '') }}
                </span>
                <p class="small text-muted mt-1 mb-0">Generated: {{ previewReport()?.createdAt | adnDate:'datetime' }}</p>
              </div>
            </div>
          </div>

          <!-- Report Summary -->
          <div class="card mb-4">
            <div class="card-header">
              <h6 class="mb-0"><i class="bi bi-bar-chart me-2"></i>Report Summary</h6>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-3">
                  <div class="summary-stat">
                    <div class="summary-stat-value">{{ previewReport()?.recordCount || 0 }}</div>
                    <div class="summary-stat-label">Total Records</div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="summary-stat">
                    <div class="summary-stat-value">{{ previewReport()?.fileSize || 'N/A' }}</div>
                    <div class="summary-stat-label">File Size</div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="summary-stat">
                    <div class="summary-stat-value">PDF</div>
                    <div class="summary-stat-label">Format</div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="summary-stat">
                    <div class="summary-stat-value">{{ previewReport()?.status === 'submitted' ? 'Yes' : 'No' }}</div>
                    <div class="summary-stat-label">Submitted</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Report Options -->
          <div class="card mb-4">
            <div class="card-header">
              <h6 class="mb-0"><i class="bi bi-gear me-2"></i>Report Options</h6>
            </div>
            <div class="card-body">
              <div class="row g-2">
                <div class="col-12 col-sm-6 col-lg-3">
                  <div class="option-item" [class.active]="previewReport()?.options?.includeChemicalDetails">
                    <i class="bi" [ngClass]="previewReport()?.options?.includeChemicalDetails ? 'bi-check-circle-fill text-success' : 'bi-x-circle text-muted'"></i>
                    <span>Chemical Details</span>
                  </div>
                </div>
                <div class="col-12 col-sm-6 col-lg-3">
                  <div class="option-item" [class.active]="previewReport()?.options?.includeApplicationRates">
                    <i class="bi" [ngClass]="previewReport()?.options?.includeApplicationRates ? 'bi-check-circle-fill text-success' : 'bi-x-circle text-muted'"></i>
                    <span>Application Rates</span>
                  </div>
                </div>
                <div class="col-12 col-sm-6 col-lg-3">
                  <div class="option-item" [class.active]="previewReport()?.options?.includeFieldMaps">
                    <i class="bi" [ngClass]="previewReport()?.options?.includeFieldMaps ? 'bi-check-circle-fill text-success' : 'bi-x-circle text-muted'"></i>
                    <span>Field Maps</span>
                  </div>
                </div>
                <div class="col-12 col-sm-6 col-lg-3">
                  <div class="option-item" [class.active]="previewReport()?.options?.includeWeatherConditions">
                    <i class="bi" [ngClass]="previewReport()?.options?.includeWeatherConditions ? 'bi-check-circle-fill text-success' : 'bi-x-circle text-muted'"></i>
                    <span>Weather Conditions</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sample Data Preview -->
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0"><i class="bi bi-table me-2"></i>Data Preview</h6>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Field</th>
                      <th>Chemical/Product</th>
                      <th>Rate</th>
                      <th>Acres</th>
                      <th>Applicator</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colspan="6" class="text-center text-muted py-3">
                        <i class="bi bi-info-circle me-1"></i>
                        Data preview available after PDF generation
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="text-center py-3 border-top">
                <span class="text-muted small">{{ previewReport()?.recordCount || 0 }} records included in this report</span>
              </div>
            </div>
          </div>
        </ng-template>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" (click)="closePreviewModal()">Close</button>
        <button
          *ngIf="previewReport() && (previewReport()?.status === 'ready' || previewReport()?.status === 'submitted')"
          type="button"
          class="btn btn-primary"
          (click)="downloadReport(previewReport()!)"
        >
          <i class="bi bi-download me-2"></i>Download PDF
        </button>
        <button
          *ngIf="previewReport() && previewReport()?.status === 'draft'"
          type="button"
          class="btn btn-primary"
          (click)="generatePdf(previewReport()!); closePreviewModal()"
        >
          <i class="bi bi-file-earmark-pdf me-2"></i>Generate PDF
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Email Modal (shared component) -->
<app-email-report-modal
  reportKind="state"
  [isOpen]="showEmailModal()"
  [report]="emailReport()"
  [reportTitle]="emailReportTitle()"
  [reportSubtitle]="emailReportSubtitle()"
  (downloadRequested)="onEmailDownloadRequested()"
  (submitted)="onEmailSubmitted($event)"
  (closed)="closeEmailModal()"
></app-email-report-modal>

<!-- Delete Confirmation Modal -->
<div class="modal-backdrop fade show" *ngIf="showDeleteConfirm()" (click)="cancelDelete()"></div>
<div class="modal fade show d-block" tabindex="-1" *ngIf="showDeleteConfirm()">
  <div class="modal-dialog modal-dialog-centered" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-exclamation-triangle text-danger me-2"></i>Delete Report
        </h5>
        <button type="button" class="btn-close" (click)="cancelDelete()"></button>
      </div>
      <div class="modal-body" *ngIf="reportToDelete()">
        <p>Are you sure you want to delete this report?</p>
        <div class="card bg-light">
          <div class="card-body py-2">
            <strong>{{ reportToDelete()?.name || getReportTypeLabel(reportToDelete()?.reportType || '') }}</strong>
            <br>
            <span class="text-muted small">{{ getStateLabel(reportToDelete()?.state || '') }} &bull; {{ formatDateRange(reportToDelete()?.startDate || '', reportToDelete()?.endDate || '') }}</span>
          </div>
        </div>

        <!-- Delete error message -->
        <div *ngIf="deleteError()" class="alert alert-danger small mt-3 mb-0">
          <i class="bi bi-exclamation-circle me-1"></i>
          {{ deleteError() }}
        </div>

        <p *ngIf="!deleteError()" class="text-danger small mt-3 mb-0">
          <i class="bi bi-exclamation-circle me-1"></i>
          This action cannot be undone.
        </p>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" (click)="cancelDelete()">Cancel</button>
        <button
          type="button"
          class="btn btn-danger"
          (click)="deleteReport()"
          [disabled]="deleteLoading()"
        >
          <ng-container *ngIf="!deleteLoading()">
            <i class="bi bi-trash me-2"></i>Delete
          </ng-container>
          <ng-container *ngIf="deleteLoading()">
            <span class="spinner-border spinner-border-sm me-2"></span>Deleting...
          </ng-container>
        </button>
      </div>
    </div>
  </div>
</div>
